import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApi } from '../../api/hooks'
import { setAuthToken } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { apiErrorCode } from '../../lib/apiError'
import { AuthLayout } from '../../components/auth/AuthLayout'

// The sign-in link mailed to a member (spec 2026-10-06-06) lands here with a
// `?token=`. The page redeems it once, on mount, and lands the member signed
// in: residents and providers sign in as their contact, mostly this way.
interface SignInLinkSearch {
    token?: string
}

export const Route = createFileRoute('/$org/sign-in-link')({
    component: SignInLinkPage,
    validateSearch: (search: Record<string, unknown>): SignInLinkSearch => ({
        token: (search.token as string) || undefined,
    }),
})

interface SignInLinkResponse {
    access_token: string
    refresh_token: string
    expires_in: number
    user: any
    default_org_code?: string
    mfa_required?: boolean
    mfa_challenge_token?: string
    mfa_enrollment_required?: boolean
}

function SignInLinkPage() {
    const { org } = Route.useParams()
    const { token } = Route.useSearch()
    const { t } = useTranslation('auth')
    const { request } = useApi()
    const { loginWithTokens } = useAuth()
    const navigate = useNavigate()
    const [error, setError] = useState<string | null>(token ? null : t('signInLink.invalid'))
    const attempted = useRef(false)

    useEffect(() => {
        if (!token || attempted.current) return
        attempted.current = true
        // The token must not linger in the address bar or the history.
        window.history.replaceState({}, '', window.location.pathname)
        ;(async () => {
            try {
                const response = (await request('/api/v1/organizations/{orgCode}/auth/sign-in-link/confirm', 'POST', {
                    params: { path: { orgCode: org } },
                    body: { token },
                })) as SignInLinkResponse
                if (response.mfa_required && response.mfa_challenge_token) {
                    navigate({
                        to: '/$org/login',
                        params: { org },
                        search: { mfa_challenge: response.mfa_challenge_token, next: undefined, email: undefined, password: undefined },
                    })
                    return
                }
                setAuthToken(response.access_token)
                loginWithTokens({
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    expires_in: response.expires_in,
                    user: response.user,
                    orgCode: org,
                    default_org_code: response.default_org_code,
                    mfa_enrollment_required: response.mfa_enrollment_required,
                })
                if (response.mfa_enrollment_required) {
                    navigate({ to: '/mfa-setup', search: { org, next: undefined } })
                    return
                }
                navigate({ to: `/${org}` })
            } catch (err) {
                setError(apiErrorCode(err) === 'sign_in_link_invalid' ? t('signInLink.invalid') : t('signInLink.failed'))
            }
        })()
    }, [token, org, request, loginWithTokens, navigate, t])

    return (
        <AuthLayout>
            <div className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-dialog" data-testid="sign-in-link-card">
                <h2 className="mb-6 text-lg font-semibold text-foreground">{t('signInLink.title')}</h2>
                {error ? (
                    <div className="space-y-4">
                        <div
                            className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded"
                            data-testid="sign-in-link-error"
                        >
                            {error}
                        </div>
                        <div className="text-center text-sm">
                            <Link
                                to="/$org/login"
                                params={{ org }}
                                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                                data-testid="sign-in-link-request-new"
                            >
                                {t('signInLink.requestNew')}
                            </Link>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground" data-testid="sign-in-link-pending">
                        {t('signInLink.signingIn')}
                    </p>
                )}
            </div>
        </AuthLayout>
    )
}
